import { useEffect, useState } from "react";
import { useLocation, NavLink } from "react-router-dom";

import { useListContext } from "../contexts/listContext";
import { useUserContext } from "../contexts/userContext";
import { UtilityService } from "../services/utilityService";

import { AppConfig } from "../config/appConfig";
import { UpdateManager } from "../classes/updateManager";

import { SignOutButton } from "./signOutButton";

import "../styles/header.scss";

import Image_Logo from "../assets/Logo.jpg";
import Image_Menu_Start from "../assets/menu_item_start.svg";
import Image_Menu_End from "../assets/menu_item_end.svg";
import Image_Menu_Start_Inverse from "../assets/menu_item_start_inverse.svg";
import Image_Menu_End_Inverse from "../assets/menu_item_end_inverse.svg";
import Image_Menu_Divider from "../assets/menu_item_divider.svg";
import Icon_Menu from "../assets/icon_menu.svg";
import Icon_Menu_Group_Open from "../assets/icon_menu_group_open.svg";
import Icon_Menu_Group_Close from "../assets/icon_menu_group_close.svg";

const Header = () => {
	const location = useLocation();
	const listContext = useListContext();
	const userContext = useUserContext();
	const [environment, setEnvironment] = useState<string | undefined>();
	const permissions = userContext.permissions;
	const [showManagementMenu, setShowManagementMenu] = useState<boolean>(false);
	const [showShipmentMenu, setShowShipmentMenu] = useState<boolean>(false);
	const [showReportMenu, setShowReportMenu] = useState<boolean>(false);
	const [showTilsMenu, setShowTilsMenu] = useState<boolean>(false);
	const [showUserMenu, setShowUserMenu] = useState<boolean>(false);
	const [hasManagementPermissions, setHasManagementPermissions] = useState(false);
	const [hasReportPermissions, setHasReportPermissions] = useState(false);

	const [showMobileNav, setShowMobileNav] = useState(false);
	const [showMobileManagementMenu, setShowMobileManagementMenu] = useState(false);
	const [showMobileShipmentMenu, setShowMobileShipmentMenu] = useState(false);
	const [showMobileReportMenu, setShowMobileReportMenu] = useState(false);
	const [showMobileTilsMenu, setShowMobileTilsMenu] = useState(false);

	const managementKeywords = [
		'appSettings',
		'customer',
		'container',
		'document',
		'employee',
		'expense',
		'material',
		'materialprice',
		'transporter',
		'vendor',
		'wastecharacterization'
	];

	const shipmentKeywords = [
		'batch',
		'batchadd',
		'shipment',
		'shippedmaterials',
		'shipmentpacket'
	];

	const reportKeywords = [
		...listContext.reportRegistrations.map(r => r.name)
	];

	const tilsKeywords = [
		'materialinventory',
		'tils',
		'trailer'
	];

	useEffect(() => {
		switch (AppConfig.environment.name) {
			case "dev": {
				setEnvironment("DEV");
				break;
			}
			case "test": {
				setEnvironment("TEST");
				break;
			}
			default: {
				setEnvironment(undefined);
				break;
			}
		}
	}, [])

	// Hook all route changes to signal user interaction for update manager
	useEffect(() => {
		UpdateManager.poke();
	}, [location])


	useEffect(() => {
		setHasManagementPermissions(
			permissions?.canManageContainers ||
			permissions?.canManageCustomers ||
			permissions?.canManageEmployees ||
			permissions?.canManageVendors ||
			permissions?.canManageTransporters ||
			permissions?.canManageMaterials ||
			permissions?.canManageMaterialPrices ||
			permissions?.canManageDocuments ||
			permissions?.canManageExpenses ||
			permissions?.canManageApplicationSettings ||
			permissions?.canManageWasteCharacterization ||
			permissions?.canViewExpensesOnly ||
			permissions?.canManageContainers);


		setHasReportPermissions(
			permissions?.canViewReportCostCredit ||
			permissions?.canViewReportExpense ||
			permissions?.canViewReportMaterialPricing ||
			permissions?.canViewReportMaterial ||
			permissions?.canViewReportMonthlySummary ||
			permissions?.canViewReportMonthlySummaryByVendor ||
			permissions?.canViewReportShipmentHistory ||
			permissions?.canViewReportShipmentByVendor ||
			permissions?.canViewReportShipmentStatus ||
			permissions?.canViewReportCustomerVendorDelta ||
			permissions?.canViewReportSummary ||
			permissions?.canViewReportClosing ||
			permissions?.canViewReportVendorsAcrossCustomers);
	}, [permissions])

	const openSubMenu = (menu: string) => {
		setShowManagementMenu(false);
		setShowShipmentMenu(false);
		setShowReportMenu(false);
		setShowTilsMenu(false);
		setShowUserMenu(false);

		setShowMobileManagementMenu(false);
		setShowMobileShipmentMenu(false);
		setShowMobileReportMenu(false);
		setShowMobileTilsMenu(false);

		switch (menu) {
			case "management": {
				setShowManagementMenu(true);
				break;
			}
			case "shipment": {
				setShowShipmentMenu(true);
				break;
			}
			case "report": {
				setShowReportMenu(true);
				break;
			}
			case "tils": {
				setShowTilsMenu(true);
				break;
			}
			case "user": {
				setShowUserMenu(true);
				break;
			}
			case "mobile-management": {
				setShowMobileManagementMenu(!showMobileManagementMenu);
				break;
			}
			case "mobile-shipment": {
				setShowMobileShipmentMenu(!showMobileShipmentMenu);
				break;
			}
			case "mobile-report": {
				setShowMobileReportMenu(!showMobileReportMenu);
				break;
			}
			case "mobile-tils": {
				setShowMobileTilsMenu(!showMobileTilsMenu);
				break;
			}
			default: {
				//no open sub-menus;
				break;
			}
		}
	}

	const isActive = (keywords: string[], urlSegment: number = 1): boolean => {
		for (let i = 0; i < keywords.length; i++) {
			let urlParts = location.pathname.split('/');
			if (urlParts.length > urlSegment - 1 && urlParts[urlSegment]?.toLowerCase() == keywords[i]?.toLowerCase()) {
				return true;
			}
		}
		return false;
	}

	return (
		<section className="hwi-header">
			<div className="wide-menu">
				<div className="brand">
					<img src={Image_Logo}></img>
					<span><strong>HWI</strong> Online</span>
				</div>
				<div className={isActive(['home']) ? "menu-item active" : "menu-item"}>
					{isActive(['home']) && <img className="start" src={Image_Menu_Start_Inverse}></img>}
					<NavLink to="/home" >
						Home
					</NavLink>
					{isActive(['home']) && <img className="end" src={Image_Menu_End_Inverse}></img>}
				</div>
				{hasManagementPermissions &&
					<div className={isActive(managementKeywords) ? "menu-item active" : "menu-item"}>
						{isActive(managementKeywords) && <img className="start" src={Image_Menu_Start_Inverse}></img>}
						<a href="#" onClick={() => openSubMenu("management")}>Management</a>
						{isActive(managementKeywords) && <img className="end" src={Image_Menu_End_Inverse}></img>}
					</div>
				}
				{permissions?.canManageShipments &&
					<div className={isActive(shipmentKeywords) ? "menu-item active" : "menu-item"}>
						{isActive(shipmentKeywords) && <img className="start" src={Image_Menu_Start_Inverse}></img>}
						<a href="#" onClick={() => openSubMenu("shipment")}>Shipments</a>
						{isActive(shipmentKeywords) && <img className="end" src={Image_Menu_End_Inverse}></img>}
					</div>
				}
				{hasReportPermissions &&
					<div className={isActive(reportKeywords, 2) ? "menu-item active" : "menu-item"}>
						{isActive(reportKeywords, 2) && <img className="start" src={Image_Menu_Start_Inverse}></img>}
						<a href="#" onClick={() => openSubMenu("report")}>Reports</a>
						{isActive(reportKeywords, 2) && <img className="end" src={Image_Menu_End_Inverse}></img>}
					</div>
				}
				{permissions?.canAccessTils && (permissions?.canManageLoads || permissions?.canManageTrailers || permissions?.canManageMaterialInventory || permissions?.canManageDeletedLoads) &&
					<div className={isActive(tilsKeywords) ? "menu-item active" : "menu-item"}>
						{isActive(tilsKeywords) && <img className="start" src={Image_Menu_Start_Inverse}></img>}
						<a href="#" onClick={() => openSubMenu("tils")}>TILS</a>
						{isActive(tilsKeywords) && <img className="end" src={Image_Menu_End_Inverse}></img>}
					</div>
				}
				<div className="user">
					<span onClick={() => openSubMenu("user")}>{userContext.displayName}</span>
					{environment &&
						<span className="environment-label">{environment}</span>
					}
				</div>
				{showManagementMenu && (
					<div className="sub-menu management-menu" onMouseLeave={() => setShowManagementMenu(false)}>
						<div className="brand"></div>
						<div className="menu-item"></div>
						<div className="contents">
							{permissions?.canManageCustomers &&
								<p><NavLink to="/customer">Customers</NavLink></p>
							}
							{permissions?.canManageEmployees &&
								<p><NavLink to="/employee" >Employees</NavLink></p>
							}
							{permissions?.canManageVendors &&
								<p><NavLink to="/vendor">Vendors</NavLink></p>
							}
							{permissions?.canManageTransporters &&
								<p><NavLink to="/transporter">Transporters</NavLink></p>
							}
							{permissions?.canManageMaterials &&
								<p><NavLink to="/material">Materials</NavLink></p>
							}
							{permissions?.canManageMaterialPrices &&
								<p><NavLink to="/materialprice">Material Prices</NavLink></p>
							}
							{permissions?.canManageContainers &&
								<p><NavLink to="/container">Containers</NavLink></p>
							}							
							{permissions?.canManageDocuments &&
								<p><NavLink to="/document">Documents</NavLink></p>
							}
							{(permissions?.canManageExpenses || permissions?.canViewExpensesOnly) &&
								<p><NavLink to="/expense">Expenses</NavLink></p>
							}
							{permissions?.canManageReportRegistrations &&
								<p><NavLink to="/report/registration">Report Registrations</NavLink></p>
							}
							{permissions?.canManageApplicationSettings &&
								<p><NavLink to="/appSettings">Settings</NavLink></p>
							}
							{permissions?.canManageWasteCharacterization &&
								<p><NavLink to="/wastecharacterization">Waste Characterizations</NavLink></p>
							}
							<div className="end-spacer"></div>
						</div>
					</div>
				)}
				{showShipmentMenu && (
					<div className="sub-menu shipment-menu" onMouseLeave={() => setShowShipmentMenu(false)}>
						<div className="brand"></div>
						{hasManagementPermissions && <div className="menu-item"></div>}
						<div className="menu-item"></div>
						<div className="contents">
							<p>
								<NavLink to="/shipment">Search Shipments</NavLink>
							</p>
							{(userContext.permissions.canReadBatches || userContext.permissions.canManageBatches) &&
								<p>
									<NavLink to="/batch">Search Batched Shipments</NavLink>
								</p>
							}
							<p>
								<NavLink to="/shippedmaterials">Shipped Materials</NavLink>
							</p>
							{(userContext.permissions.canUseShipmentPacketAudit) &&
								<p>
									<NavLink to="/shipmentpacket">Packet Audit</NavLink>
								</p>
							}
							{(userContext.permissions.canManageDeletedShipments) &&
								<p>
									<NavLink to="/shipment/deleted">Deleted Shipments</NavLink>
								</p>
							}
						</div>
					</div>
				)}
				{showReportMenu && (
					<div className="sub-menu report-menu" onMouseLeave={() => setShowReportMenu(false)}>
						<div className="brand"></div>
						{hasManagementPermissions && <div className="menu-item"></div>}
						{permissions?.canManageShipments && <div className="menu-item"></div>}
						<div className="menu-item"></div>
						<div className="contents">
							{listContext?.reportRegistrations.filter(report => report.ordinal > 0 && (UtilityService.getParameterCaseInsensitive(permissions, report.permission) ?? true) === true).map((report) => (
								<p key={report.id}>
									<NavLink to={`/report/${report.name}`}>{report.displayName}</NavLink>
								</p>
							))}
						</div>
					</div>
				)}
				{showTilsMenu && (
					<div className="sub-menu tils-menu" onMouseLeave={() => setShowTilsMenu(false)}>
						<div className="brand"></div>
						{hasManagementPermissions && <div className="menu-item"></div>}
						{permissions?.canManageShipments && <div className="menu-item"></div>}
						{hasReportPermissions && <div className="menu-item"></div>}
						<div className="menu-item"></div>
						<div className="contents">
							{permissions?.canManageMaterialInventory &&
								<p><NavLink to="/materialinventory">Materials</NavLink></p>
							}
							{permissions?.canManageLoads &&
								<p><NavLink to="/tils">Loads</NavLink></p>
							}
							{permissions?.canManageTrailers &&
								<p><NavLink to="/trailer">Trailers</NavLink></p>
							}
							{permissions?.canManageDeletedLoads &&
								<p><NavLink to="/tils/deleted">Deleted Loads</NavLink></p>
							}
						</div>
					</div>
				)}
				{showUserMenu && (
					<div className="sub-menu user-menu" onMouseLeave={() => setShowUserMenu(false)}>
						<p>
							<SignOutButton></SignOutButton>
						</p>
					</div>
				)}
			</div>








			<div className="mobile-menu">
				<div className="controls">
					<div className="brand">
						<img src={Image_Logo}></img>
						<span><strong>HWI</strong> Online</span>
					</div>
					<div className="toggle" onClick={() => setShowMobileNav(!showMobileNav)}>
						<img src={Icon_Menu}></img>
					</div>
				</div>
				{showMobileNav &&
					<div className="nav-menu">
						<NavLink to="/home" className="menu-item">
							<div className="title">
								Home
							</div>
							<div className="type">
								<span></span>
							</div>
						</NavLink>
						{hasManagementPermissions && (
							<div className="menu-item" onClick={() => openSubMenu("mobile-management")}>
								<div className="title">
									Management
								</div>
								<div className="opener">
									{showMobileManagementMenu ? (
										<img className="" src={Icon_Menu_Group_Close}></img>
									) : (
										<img className="" src={Icon_Menu_Group_Open}></img>
									)}
								</div>
							</div>
						)}
						{showMobileManagementMenu && (
							<>
								{userContext.permissions?.canManageCustomers && (<NavLink to="/customer" className="menu-item level-two">Customers</NavLink>)}
								{userContext.permissions?.canManageEmployees && (<NavLink to="/employee" className="menu-item level-two">Employees</NavLink>)}
								{userContext.permissions?.canManageVendors && (<NavLink to="/vendor" className="menu-item level-two">Vendors</NavLink>)}
								{userContext.permissions?.canManageTransporters && (<NavLink to="/transporter" className="menu-item level-two">Transporters</NavLink>)}
								{userContext.permissions?.canManageMaterials && (<NavLink to="/material" className="menu-item level-two">Materials</NavLink>)}
								{userContext.permissions?.canManageMaterialPrices && (<NavLink to="/materialprices" className="menu-item level-two">Material Prices</NavLink>)}								
								{userContext.permissions?.canManageContainers && (<NavLink to="/container" className="menu-item level-two">Containers</NavLink>)}
								{userContext.permissions?.canManageDocuments && (<NavLink to="/document" className="menu-item level-two">Documents</NavLink>)}
								{(userContext.permissions?.canManageExpenses || userContext.permissions?.canViewExpensesOnly) && (<NavLink to="/expense" className="menu-item level-two">Expense</NavLink>)}
								{userContext.permissions?.canManageReportRegistrations && (<NavLink to="/report/registration" className="menu-item level-two">Report Registrations</NavLink>)}
								{userContext.permissions?.canManageApplicationSettings && (<NavLink to="/appSettings" className="menu-item level-two">Settings</NavLink>)}
								{userContext.permissions?.canManageWasteCharacterization && (<NavLink to="/wastecharacterization" className="menu-item level-two">Waste Characterizations</NavLink>)}
							</>
						)}
						{permissions?.canManageShipments && (
							<div className="menu-item" onClick={() => openSubMenu("mobile-shipment")}>
								<div className="title">
									Shipments
								</div>
								<div className="opener">
									{showMobileShipmentMenu ? (
										<img className="" src={Icon_Menu_Group_Close}></img>
									) : (
										<img className="" src={Icon_Menu_Group_Open}></img>
									)}
								</div>
							</div>
						)}
						{showMobileShipmentMenu && (
							<>
								<NavLink to="/shipment" className="menu-item level-two">Search Shipments</NavLink>
								{(userContext.permissions.canReadBatches || userContext.permissions.canManageBatches) &&
									<NavLink to="/batch" className="menu-item level-two">Search Batched Shipments</NavLink>
								}
								<NavLink to="/shippedmaterials" className="menu-item level-two">Shipped Materials</NavLink>
								{(userContext.permissions.canUseShipmentPacketAudit) &&
									<NavLink to="/shipmentpacket" className="menu-item level-two">Packet Audit</NavLink>
								}
								{(userContext.permissions.canManageDeletedShipments) &&
									<NavLink to="/shipment/deleted" className="menu-item level-two">Deleted Shipments</NavLink>
								}
							</>
						)}
						{hasReportPermissions && (
							<div className="menu-item" onClick={() => openSubMenu("mobile-report")}>
								<div className="title">
									Reports
								</div>
								<div className="opener">
									{showMobileReportMenu ? (
										<img className="" src={Icon_Menu_Group_Close}></img>
									) : (
										<img className="" src={Icon_Menu_Group_Open}></img>
									)}
								</div>
							</div>
						)}
						{showMobileReportMenu && (
							<>
								{listContext?.reportRegistrations.filter(report => report.ordinal > 0 && UtilityService.getParameterCaseInsensitive(permissions, report.permission) === true).map((report) => (
									<NavLink to={`/report/${report.name}`} className="menu-item level-two">{report.displayName}</NavLink>
								))}
							</>
						)}
						{permissions?.canAccessTils && (permissions?.canManageLoads || permissions?.canManageTrailers || permissions?.canManageMaterialInventory || permissions?.canManageDeletedLoads) && (
							<div className="menu-item" onClick={() => openSubMenu("mobile-tils")}>
								<div className="title">
									TILS
								</div>
								<div className="opener">
									{showMobileTilsMenu ? (
										<img className="" src={Icon_Menu_Group_Close}></img>
									) : (
										<img className="" src={Icon_Menu_Group_Open}></img>
									)}
								</div>
							</div>
						)}
						{showMobileTilsMenu && (
							<>
								{userContext.permissions.canManageMaterialInventory &&
									<NavLink to="/materialinventory" className="menu-item level-two">Materials</NavLink>
								}
								{userContext.permissions.canManageLoads &&
									<NavLink to="/tils" className="menu-item level-two">Loads</NavLink>
								}
								{userContext.permissions.canManageTrailers &&
									<NavLink to="/trailer" className="menu-item level-two">Trailers</NavLink>
								}
								{userContext.permissions.canManageDeletedLoads &&
									<NavLink to="/tils/deleted" className="menu-item level-two">Deleted Loads</NavLink>
								}
							</>
						)}
						<div className="menu-item">
							<div className="sign-out">
								<SignOutButton></SignOutButton>
							</div>							
						</div>
					</div>
				}
			</div>
		</section>
	);
};

export default Header;
